import React, { useEffect, useState, useCallback } from "react";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  Badge,
  Button,
  TabGroup,
  Input,
  Select,
  FormGroup,
  Table,
  Thead,
  Th,
  Td,
  TRow,
  EmptyState,
  AlertBanner,
} from "../ui";

// ============================================================
// TYPES
// ============================================================

interface EventSettings {
  discount_tiers: Array<{ min_amount: number; discount_pct: number }>;
  free_delivery_threshold: number;
  deposit_pct: number;
  lead_time_days: number;
}

interface LineItem {
  description: string;
  qty: number;
  unit_price: number;
}

interface EventOrder {
  id: string;
  event_type: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  event_date: string;
  event_location: string;
  guest_count: number;
  items: LineItem[];
  subtotal: number;
  discount_pct: number;
  discount_amount: number;
  delivery_fee: number;
  tax: number;
  total: number;
  deposit_required: number;
  deposit_paid: boolean;
  status: string;
  notes: string;
  internal_notes: string;
  created_at: string;
}

type Tab = "active" | "new" | "settings" | "history";

const EVENT_TYPES = ["wedding", "party", "corporate", "fundraiser"];

const STATUS_FLOW = ["Inquiry", "Quoted", "Confirmed", "Delivered", "Completed"];

const STATUS_VARIANTS: Record<string, "default" | "gold" | "blue" | "green" | "danger" | "purple"> = {
  Inquiry: "blue",
  Quoted: "gold",
  Confirmed: "purple",
  Delivered: "green",
  Completed: "default",
  Cancelled: "danger",
};

const EVENT_TYPE_VARIANTS: Record<string, "default" | "gold" | "blue" | "green" | "danger" | "purple"> = {
  wedding: "purple",
  party: "gold",
  corporate: "blue",
  fundraiser: "green",
};

const DEFAULT_SETTINGS: EventSettings = {
  discount_tiers: [
    { min_amount: 500, discount_pct: 5 },
    { min_amount: 1000, discount_pct: 8 },
    { min_amount: 2000, discount_pct: 10 },
    { min_amount: 5000, discount_pct: 12 },
  ],
  free_delivery_threshold: 500,
  deposit_pct: 25,
  lead_time_days: 14,
};

const TAX_RATE = 0.06;

// ============================================================
// HELPERS
// ============================================================

function getDiscount(subtotal: number, tiers: EventSettings["discount_tiers"]): number {
  let pct = 0;
  const sorted = [...tiers].sort((a, b) => a.min_amount - b.min_amount);
  for (const tier of sorted) {
    if (subtotal >= tier.min_amount) pct = tier.discount_pct;
  }
  return pct;
}

function getNextStatus(current: string): string | null {
  const idx = STATUS_FLOW.indexOf(current);
  if (idx < 0 || idx >= STATUS_FLOW.length - 1) return null;
  return STATUS_FLOW[idx + 1];
}

// ============================================================
// MAIN PAGE
// ============================================================

export default function EventsPage() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  const [tab, setTab] = useState<Tab>("active");
  const [settings, setSettings] = useState<EventSettings>(DEFAULT_SETTINGS);
  const [orders, setOrders] = useState<EventOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Load settings
  useEffect(() => {
    api.get("/events/settings").then((res) => {
      if (res.data && res.data.discount_tiers) {
        setSettings(res.data);
      }
    }).catch(() => {
      // Use defaults
    });
  }, []);

  // Load orders
  const fetchOrders = useCallback(async (status?: string) => {
    try {
      setLoading(true);
      const params = status ? `?status=${status}` : "";
      const res = await api.get(`/events/orders${params}`);
      setOrders(res.data || []);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "active") {
      fetchOrders();
    } else if (tab === "history") {
      fetchOrders("Completed");
    }
  }, [tab, fetchOrders]);

  const tabs = [
    { key: "active", label: "Active Events" },
    { key: "new", label: "New Event Quote" },
    ...(isOwner ? [{ key: "settings", label: "Settings" }] : []),
    { key: "history", label: "History" },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Events & Catering"
        subtitle="Manage event orders, quotes, and volume discounts"
      />

      {error && <AlertBanner variant="danger">{error}</AlertBanner>}
      {success && <AlertBanner variant="green">{success}</AlertBanner>}

      <TabGroup
        tabs={tabs}
        active={tab}
        onChange={(key) => {
          setTab(key as Tab);
          setError("");
          setSuccess("");
        }}
      />

      {tab === "active" && (
        <ActiveEvents
          orders={orders.filter((o) => o.status !== "Completed" && o.status !== "Cancelled")}
          loading={loading}
          onUpdateStatus={async (id, status) => {
            try {
              await api.put(`/events/orders/${id}`, { status });
              setSuccess(`Order updated to ${status}`);
              fetchOrders();
            } catch {
              setError("Failed to update order status");
            }
          }}
          onToggleDeposit={async (id, paid) => {
            try {
              await api.put(`/events/orders/${id}`, { deposit_paid: paid });
              setSuccess("Deposit status updated");
              fetchOrders();
            } catch {
              setError("Failed to update deposit status");
            }
          }}
        />
      )}

      {tab === "new" && (
        <NewEventQuote
          settings={settings}
          onSubmit={async (data) => {
            try {
              setError("");
              await api.post("/events/orders", data);
              setSuccess("Event quote created successfully!");
              setTab("active");
              fetchOrders();
            } catch {
              setError("Failed to create event quote");
            }
          }}
        />
      )}

      {tab === "settings" && isOwner && (
        <SettingsPanel
          settings={settings}
          onSave={async (updated) => {
            try {
              setError("");
              await api.put("/events/settings", updated);
              setSettings(updated);
              setSuccess("Settings saved");
            } catch {
              setError("Failed to save settings");
            }
          }}
        />
      )}

      {tab === "history" && (
        <HistoryTable
          orders={orders}
          loading={loading}
        />
      )}
    </PageContainer>
  );
}

// ============================================================
// ACTIVE EVENTS
// ============================================================

function ActiveEvents({
  orders,
  loading,
  onUpdateStatus,
  onToggleDeposit,
}: {
  orders: EventOrder[];
  loading: boolean;
  onUpdateStatus: (id: string, status: string) => void;
  onToggleDeposit: (id: string, paid: boolean) => void;
}) {
  if (loading) {
    return <EmptyState message="Loading events..." />;
  }
  if (orders.length === 0) {
    return <EmptyState message="No active events" detail="Create a new event quote to get started" icon="&#127881;" />;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
      {orders.map((order) => {
        const nextStatus = getNextStatus(order.status);
        return (
          <Card key={order.id}>
            <div className="flex items-center justify-between mb-3">
              <Badge variant={EVENT_TYPE_VARIANTS[order.event_type] || "default"}>
                {order.event_type}
              </Badge>
              <Badge variant={STATUS_VARIANTS[order.status] || "default"}>
                {order.status}
              </Badge>
            </div>

            <h3 className="text-lg font-bold mb-1" style={{ color: "var(--rbw-navy)" }}>
              {order.customer_name}
            </h3>

            <div className="flex flex-col gap-1 text-sm mb-4" style={{ color: "var(--rbw-gray-dark)" }}>
              <div className="flex justify-between">
                <span>Event Date</span>
                <span className="font-medium" style={{ color: "var(--rbw-navy)" }}>
                  {new Date(order.event_date).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Guests</span>
                <span className="font-medium" style={{ color: "var(--rbw-navy)" }}>
                  {order.guest_count}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Total</span>
                <span className="font-bold" style={{ color: "var(--rbw-navy)" }}>
                  ${Number(order.total).toFixed(2)}
                </span>
              </div>
              {order.discount_pct > 0 && (
                <div className="flex justify-between">
                  <span>Discount</span>
                  <span className="font-medium" style={{ color: "var(--rbw-success)" }}>
                    {order.discount_pct}% off
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Deposit ({Math.round((order.deposit_required / order.total) * 100)}%)</span>
                <span className="font-medium" style={{ color: order.deposit_paid ? "var(--rbw-success)" : "var(--rbw-danger)" }}>
                  ${Number(order.deposit_required).toFixed(2)} - {order.deposit_paid ? "PAID" : "UNPAID"}
                </span>
              </div>
            </div>

            {order.notes && (
              <p className="text-xs mb-3 p-2 rounded-lg" style={{ background: "var(--rbw-bg)", color: "var(--rbw-gray-dark)" }}>
                {order.notes}
              </p>
            )}

            <div className="flex gap-2 mt-auto">
              <button
                onClick={() => onToggleDeposit(order.id, !order.deposit_paid)}
                className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
                style={{
                  background: order.deposit_paid ? "#2D8A4E15" : "#F6BE0015",
                  color: order.deposit_paid ? "var(--rbw-success)" : "var(--rbw-gold-dark)",
                  border: `1px solid ${order.deposit_paid ? "#2D8A4E40" : "#F6BE0040"}`,
                }}
              >
                {order.deposit_paid ? "Deposit Paid" : "Mark Deposit Paid"}
              </button>
              {nextStatus && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onUpdateStatus(order.id, nextStatus)}
                >
                  {nextStatus}
                </Button>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ============================================================
// NEW EVENT QUOTE
// ============================================================

interface QuoteFormData {
  event_type: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  event_date: string;
  event_location: string;
  guest_count: number;
  items: LineItem[];
  notes: string;
}

function NewEventQuote({
  settings,
  onSubmit,
}: {
  settings: EventSettings;
  onSubmit: (data: QuoteFormData) => void;
}) {
  const [form, setForm] = useState<QuoteFormData>({
    event_type: "wedding",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    event_date: "",
    event_location: "",
    guest_count: 50,
    items: [{ description: "", qty: 1, unit_price: 0 }],
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const updateField = <K extends keyof QuoteFormData>(key: K, value: QuoteFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateItem = (index: number, field: keyof LineItem, value: string | number) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, { description: "", qty: 1, unit_price: 0 }],
    }));
  };

  const removeItem = (index: number) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  // Calculations
  const subtotal = form.items.reduce((sum, item) => sum + item.qty * item.unit_price, 0);
  const discountPct = getDiscount(subtotal, settings.discount_tiers);
  const discountAmount = subtotal * (discountPct / 100);
  const afterDiscount = subtotal - discountAmount;
  const freeDelivery = subtotal >= settings.free_delivery_threshold;
  const deliveryFee = freeDelivery ? 0 : 25;
  const tax = afterDiscount * TAX_RATE;
  const total = afterDiscount + tax + deliveryFee;
  const depositRequired = total * (settings.deposit_pct / 100);

  const canSubmit = form.customer_name.trim() && form.customer_phone.trim() &&
    form.event_date && form.items.length > 0 &&
    form.items.every((i) => i.description.trim() && i.qty > 0 && i.unit_price > 0);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    await onSubmit(form);
    setSubmitting(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Form */}
      <div className="lg:col-span-2">
        <Card>
          <CardHeader title="Event Details" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <FormGroup label="Event Type">
              <Select value={form.event_type} onChange={(v) => updateField("event_type", v)}>
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup label="Event Date">
              <Input
                type="date"
                value={form.event_date}
                onChange={(v) => updateField("event_date", v)}
                required
              />
            </FormGroup>
            <FormGroup label="Customer Name">
              <Input
                value={form.customer_name}
                onChange={(v) => updateField("customer_name", v)}
                placeholder="Full name"
                required
              />
            </FormGroup>
            <FormGroup label="Phone">
              <Input
                type="tel"
                value={form.customer_phone}
                onChange={(v) => updateField("customer_phone", v)}
                placeholder="(555) 555-5555"
                required
              />
            </FormGroup>
            <FormGroup label="Email">
              <Input
                type="text"
                value={form.customer_email}
                onChange={(v) => updateField("customer_email", v)}
                placeholder="email@example.com"
              />
            </FormGroup>
            <FormGroup label="Guest Count">
              <Input
                type="number"
                value={form.guest_count}
                onChange={(v) => updateField("guest_count", parseInt(v) || 0)}
                min="1"
              />
            </FormGroup>
            <FormGroup label="Event Location" span={2}>
              <Input
                value={form.event_location}
                onChange={(v) => updateField("event_location", v)}
                placeholder="Venue name and address"
              />
            </FormGroup>
          </div>

          <CardHeader title="Line Items" />
          <div className="flex flex-col gap-3 mb-4">
            {form.items.map((item, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-5">
                  {idx === 0 && (
                    <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>
                      Description
                    </label>
                  )}
                  <Input
                    value={item.description}
                    onChange={(v) => updateItem(idx, "description", v)}
                    placeholder="e.g. Case of Yuengling Lager"
                  />
                </div>
                <div className="col-span-2">
                  {idx === 0 && (
                    <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>
                      Qty
                    </label>
                  )}
                  <Input
                    type="number"
                    value={item.qty}
                    onChange={(v) => updateItem(idx, "qty", parseInt(v) || 0)}
                    min="1"
                  />
                </div>
                <div className="col-span-3">
                  {idx === 0 && (
                    <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>
                      Unit Price
                    </label>
                  )}
                  <Input
                    type="number"
                    value={item.unit_price}
                    onChange={(v) => updateItem(idx, "unit_price", parseFloat(v) || 0)}
                    step="0.01"
                    min="0"
                    placeholder="$0.00"
                  />
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <span className="text-sm font-bold whitespace-nowrap" style={{ color: "var(--rbw-navy)" }}>
                    ${(item.qty * item.unit_price).toFixed(2)}
                  </span>
                  {form.items.length > 1 && (
                    <button
                      onClick={() => removeItem(idx)}
                      className="p-1 rounded transition-colors"
                      style={{ color: "var(--rbw-danger)" }}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Button variant="ghost" size="sm" onClick={addItem}>
            + Add Line Item
          </Button>

          <div className="mt-6">
            <FormGroup label="Notes" span={2}>
              <textarea
                value={form.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                placeholder="Special requests, delivery instructions, etc."
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl outline-none transition-all"
                style={{
                  border: "2px solid var(--rbw-border)",
                  background: "var(--rbw-bg-card)",
                  color: "var(--rbw-navy)",
                  resize: "vertical",
                }}
                onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
                onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
              />
            </FormGroup>
          </div>
        </Card>
      </div>

      {/* Quote Summary */}
      <div>
        <Card>
          <CardHeader title="Quote Summary" />
          <div className="flex flex-col gap-2 text-sm">
            <div className="flex justify-between" style={{ color: "var(--rbw-gray-dark)" }}>
              <span>Subtotal</span>
              <span className="font-medium" style={{ color: "var(--rbw-navy)" }}>${subtotal.toFixed(2)}</span>
            </div>

            {discountPct > 0 && (
              <div className="flex justify-between" style={{ color: "var(--rbw-success)" }}>
                <span>Discount ({discountPct}%)</span>
                <span className="font-medium">-${discountAmount.toFixed(2)}</span>
              </div>
            )}

            <div className="flex justify-between" style={{ color: "var(--rbw-gray-dark)" }}>
              <span>Tax (6%)</span>
              <span className="font-medium" style={{ color: "var(--rbw-navy)" }}>${tax.toFixed(2)}</span>
            </div>

            <div className="flex justify-between" style={{ color: "var(--rbw-gray-dark)" }}>
              <span>Delivery</span>
              <span className="font-medium" style={{ color: freeDelivery ? "var(--rbw-success)" : "var(--rbw-navy)" }}>
                {freeDelivery ? "FREE" : `$${deliveryFee.toFixed(2)}`}
              </span>
            </div>

            <div
              className="flex justify-between pt-3 mt-2 text-base font-bold"
              style={{ borderTop: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}
            >
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>

            <div
              className="flex justify-between pt-2 mt-1"
              style={{ color: "var(--rbw-gold-dark)" }}
            >
              <span className="font-medium">Deposit Required ({settings.deposit_pct}%)</span>
              <span className="font-bold">${depositRequired.toFixed(2)}</span>
            </div>
          </div>

          {/* Discount tiers info */}
          <div className="mt-6 p-3 rounded-xl" style={{ background: "var(--rbw-bg)" }}>
            <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "var(--rbw-gray-dark)" }}>
              Volume Discounts
            </div>
            {settings.discount_tiers.map((tier, idx) => (
              <div
                key={idx}
                className="flex justify-between text-xs py-1"
                style={{ color: subtotal >= tier.min_amount ? "var(--rbw-success)" : "var(--rbw-gray-dark)" }}
              >
                <span>${tier.min_amount.toLocaleString()}+</span>
                <span className="font-semibold">{tier.discount_pct}% off + free delivery</span>
              </div>
            ))}
          </div>

          <Button
            variant="primary"
            size="lg"
            className="w-full mt-6"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
          >
            {submitting ? "Creating Quote..." : "Create Quote"}
          </Button>
        </Card>
      </div>
    </div>
  );
}

// ============================================================
// SETTINGS PANEL
// ============================================================

function SettingsPanel({
  settings,
  onSave,
}: {
  settings: EventSettings;
  onSave: (settings: EventSettings) => void;
}) {
  const [form, setForm] = useState<EventSettings>({ ...settings });
  const [saving, setSaving] = useState(false);

  const updateTier = (index: number, field: "min_amount" | "discount_pct", value: number) => {
    setForm((prev) => {
      const tiers = [...prev.discount_tiers];
      tiers[index] = { ...tiers[index], [field]: value };
      return { ...prev, discount_tiers: tiers };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="max-w-2xl">
      <Card>
        <CardHeader title="Event & Catering Settings" />

        <div className="mb-6">
          <h3 className="text-sm font-bold mb-3" style={{ color: "var(--rbw-navy)" }}>
            Discount Tiers
          </h3>
          <div className="flex flex-col gap-3">
            {form.discount_tiers.map((tier, idx) => (
              <div key={idx} className="grid grid-cols-2 gap-3">
                <FormGroup label={`Minimum Amount (Tier ${idx + 1})`}>
                  <Input
                    type="number"
                    value={tier.min_amount}
                    onChange={(v) => updateTier(idx, "min_amount", parseFloat(v) || 0)}
                    step="100"
                    min="0"
                  />
                </FormGroup>
                <FormGroup label="Discount %">
                  <Input
                    type="number"
                    value={tier.discount_pct}
                    onChange={(v) => updateTier(idx, "discount_pct", parseFloat(v) || 0)}
                    step="1"
                    min="0"
                  />
                </FormGroup>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <FormGroup label="Free Delivery Threshold ($)">
            <Input
              type="number"
              value={form.free_delivery_threshold}
              onChange={(v) => setForm((prev) => ({ ...prev, free_delivery_threshold: parseFloat(v) || 0 }))}
              step="50"
              min="0"
            />
          </FormGroup>
          <FormGroup label="Deposit %">
            <Input
              type="number"
              value={form.deposit_pct}
              onChange={(v) => setForm((prev) => ({ ...prev, deposit_pct: parseFloat(v) || 0 }))}
              step="5"
              min="0"
            />
          </FormGroup>
          <FormGroup label="Lead Time (days)">
            <Input
              type="number"
              value={form.lead_time_days}
              onChange={(v) => setForm((prev) => ({ ...prev, lead_time_days: parseInt(v) || 0 }))}
              min="0"
            />
          </FormGroup>
        </div>

        <Button onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </Card>
    </div>
  );
}

// ============================================================
// HISTORY TABLE
// ============================================================

function HistoryTable({
  orders,
  loading,
}: {
  orders: EventOrder[];
  loading: boolean;
}) {
  if (loading) {
    return <EmptyState message="Loading history..." />;
  }
  if (orders.length === 0) {
    return <EmptyState message="No completed events yet" detail="Completed event orders will appear here" />;
  }

  return (
    <Table>
      <Thead>
        <Th>Date</Th>
        <Th>Customer</Th>
        <Th>Type</Th>
        <Th>Guests</Th>
        <Th align="right">Total</Th>
        <Th align="right">Discount</Th>
        <Th align="center">Status</Th>
      </Thead>
      <tbody>
        {orders.map((order, idx) => (
          <TRow key={order.id} index={idx}>
            <Td>{new Date(order.event_date).toLocaleDateString()}</Td>
            <Td bold>{order.customer_name}</Td>
            <Td>
              <Badge variant={EVENT_TYPE_VARIANTS[order.event_type] || "default"}>
                {order.event_type}
              </Badge>
            </Td>
            <Td>{order.guest_count}</Td>
            <Td align="right" bold>${Number(order.total).toFixed(2)}</Td>
            <Td align="right">{order.discount_pct > 0 ? `${order.discount_pct}%` : "-"}</Td>
            <Td align="center">
              <Badge variant={STATUS_VARIANTS[order.status] || "default"}>
                {order.status}
              </Badge>
            </Td>
          </TRow>
        ))}
      </tbody>
    </Table>
  );
}
